import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { useContextSelector } from "use-context-selector";
import { apiGet } from "../../Utils/apiFetch";
import { apiPath } from "../../Utils/apiPath";
import {
  user_status,
  levelMap,
  toFixedWithoutRounding,
} from "../../Utils/Constant";
import AuthContext from "../../context/AuthContext";
import { Link } from "react-router-dom";

// Splits runners using the event name ("Team A v Team B"): Team A first,
// Team B last, any remaining runner (The Draw) in the middle.
// Falls back to array order if the names can't be matched.
const splitRunnersByEvent = (runners = [], eventName = "") => {
  const list = runners || [];
  const [n1, n2] = String(eventName || "")
    .split(/\s+(?:v|vs\.?)\s+/i)
    .map((s) => s.trim().toLowerCase());
  const same = (r, n) => {
    const rn = String(r?.RunnerName || "").trim().toLowerCase();
    return !!n && !!rn && (rn === n || rn.includes(n) || n.includes(rn));
  };
  let team1 = list.find((r) => same(r, n1));
  let team2 = list.find((r) => r !== team1 && same(r, n2));
  let draw = null;

  if (team1 && team2) {
    draw = list.find((r) => r !== team1 && r !== team2) || null;
  } else if (list.length > 2) {
    draw =
      list.find((r) => /draw/i.test(r?.RunnerName || "")) ||
      list[list.length - 1];
    const teams = list.filter((r) => r !== draw);
    team1 = teams[0];
    team2 = teams[1];
  } else {
    team1 = list[0];
    team2 = list[1];
  }
  return { team1, team2, draw };
};

export default function RiskModal({
  isOpen,
  onClose,
  eventId,
  marketId,
  selectionId,
  type,
  userId,
  userType,
}) {
  const user = useContextSelector(AuthContext, (ctx) => ctx.user);
  const isSportsBook = type == "SportsBook";

  // Hierarchy lives in React state only. It is tied to the
  // modal context key, so a different event/market/user never sees an old path.
  const ctxKey = `${eventId}|${marketId}|${userId}|${userType}`;
  const [hierarchyState, setHierarchyState] = useState({
    key: ctxKey,
    list: [],
  });
  const hierarchy = hierarchyState.key === ctxKey ? hierarchyState.list : [];
  const setHierarchy = (list) => setHierarchyState({ key: ctxKey, list });

  const current =
    hierarchy.length > 0
      ? {
          userId: hierarchy[hierarchy.length - 1]._id,
          userType: hierarchy[hierarchy.length - 1].userType,
        }
      : { userId, userType };

  const [detailsData, setDetailsData] = useState({});
  const [matchData, setMatchData] = useState([]);
  const [isLoader, setLoader] = useState(false);

  // --- SportsBook: flat win/loss rows, separate from matchData's shape ---
  const [sportPremiumData, setSportPremiumData] = useState([]);

  // On close: clear the path and all loaded data so reopening never shows
  // the previous match's numbers.
  useEffect(() => {
    if (!isOpen) {
      setHierarchyState({ key: ctxKey, list: [] });
      setDetailsData({});
      setMatchData([]);
      setSportPremiumData([]);
    }
  }, [isOpen]);

  const betType =
    type === "Match Odds"
      ? "betfair"
      : type === "Fancy Bet"
        ? "fancy"
        : type === "Book Maker"
          ? "bookmaker"
          : type === "Toss"
            ? "toss"
            : "";

  // Event details (names + runners): depends only on event/market
  useEffect(() => {
    if (!isOpen || !eventId || !marketId || isSportsBook) return;
    let cancelled = false;
    (async () => {
      const { status, data } = await apiGet(
        apiPath.riskManagementDetails +
          `?eventId=${eventId}&marketId=${marketId}`,
      );
      if (cancelled) return;
      if (status === 200 && data.success && data.results) {
        setDetailsData(data.results);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, eventId, marketId, isSportsBook]);

  // Downline rows: refetched on every drill-down. The `cancelled` flag drops
  // late responses from an older request, so stale data can't overwrite new.
  useEffect(() => {
    if (!isOpen || !eventId || !marketId) return;
    let cancelled = false;

    const run = async () => {
      setLoader(true);
      try {
        if (isSportsBook) {
          const { status, data } = await apiGet(
            apiPath.riskManagementSPortPremiumDetails +
              `?eventId=${eventId}&selectionId=${selectionId}&marketId=${marketId}&userType=${current?.userType}&userId=${current?.userId}`,
          );
          if (cancelled) return;
          setSportPremiumData(
            status === 200 && data.success && data.results ? data.results : [],
          );
        } else {
          const { status, data } = await apiGet(apiPath.downlineDataRisk, {
            userId: current?.userId,
            userType: current?.userType,
            eventId,
            marketId,
            betType,
          });
          if (cancelled) return;
          setMatchData(
            status === 200 && data.success && data.results ? data.results : [],
          );
        }
      } finally {
        if (!cancelled) setLoader(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    eventId,
    marketId,
    selectionId,
    betType,
    isSportsBook,
    current?.userId,
    current?.userType,
  ]);

  if (!isOpen) return null;

  // Team 1 / Team 2 from the event name, The Draw in the middle
  const {
    team1: runner1,
    team2: runner2,
    draw: runnerDraw,
  } = splitRunnersByEvent(detailsData?.jsonData, detailsData?.eventName);

  const positionFor = (row, sel) =>
    row?.position?.find((p) => p?.selectionId == sel)?.position ?? 0;

  const totals = matchData.reduce(
    (acc, row) => {
      acc.first += positionFor(row, runner1?.SelectionId);
      acc.second += positionFor(row, runner2?.SelectionId);
      acc.draw += runnerDraw ? positionFor(row, runnerDraw?.SelectionId) : 0;
      return acc;
    },
    { first: 0, second: 0, draw: 0 },
  );

  // --- SportsBook totals: sum of positionProfitAmount / positionLoseAmount
  // across rows, same as ViewSportPremium.jsx ---
  const sportPremiumTotals = sportPremiumData.reduce(
    (acc, row) => {
      acc.profit += row?.positionProfitAmount || 0;
      acc.lose += row?.positionLoseAmount || 0;
      return acc;
    },
    { profit: 0, lose: 0 },
  );

  // Each cell is colored by its own sign (red = negative, green = positive)
  const plSpan = (value) => {
    const v = Number(value ?? 0);
    const cls = Math.sign(v) === -1 ? "red" : "green";
    const label =
      Math.sign(v) === -1 ? `(${Math.abs(v).toFixed(2)})` : v.toFixed(2);
    return <span className={cls}>{label}</span>;
  };

  const drillInto = (row) => {
    setHierarchy([
      ...hierarchy,
      { _id: row._id, username: row.username, userType: row?.userType },
    ]);
  };

  // --- SportsBook: user lives under row.profileData, not at row top level ---
  const drillIntoSportsBook = (row) => {
    setHierarchy([
      ...hierarchy,
      {
        _id: row?.profileData?._id,
        username: row?.profileData?.username,
        userType: row?.profileData?.userType,
      },
    ]);
  };

  const jumpToLevel = (index) => {
    setHierarchy(hierarchy.slice(0, index + 1));
  };

  return (
    <div className="risk-inner-scope">
      <div
        id="createModal"
        className="pop_bg"
        style={{ height: "100%", width: "100%", display: "block" }}
        onClick={() => onClose?.()}
      >
        <div
          className="log-wrap"
          style={{ marginTop: "9vh", overflow: "auto" }}
          onClick={(e) => e.stopPropagation()}
        >
          <table className="game-team table-responsive">
            <tbody>
              <tr>
                <td className="game-name">
                  <div className="wd">
                    {isSportsBook
                      ? type
                      : `${detailsData?.eventName || ""} - ${type}`}
                  </div>
                  <a
                    className="pop-close"
                    style={{ top: "-1px", display: "block", cursor: "pointer" }}
                    onClick={() => onClose?.()}
                  >
                    <img
                      src="data:image/gif;base64,R0lGODdhAQABAIAAAAAAAP///yH5BAEAAAEALAAAAAABAAEAAAICTAEAOw=="
                      className="icon-back"
                    />
                    Close
                  </a>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="agent_path">
            <ul id="agentPath" className="agent_path-L">
              <li className="breadcrumb-li">
                <a
                  onClick={() => jumpToLevel(-1)}
                  style={{ cursor: "pointer" }}
                >
                  <span className={levelMap[user?.userType] || ""} />
                  <strong>{user?.username}</strong>
                </a>
              </li>
              {hierarchy.map((h, i) => (
                <li className="breadcrumb-li" key={h._id}>
                  <a
                    onClick={() => jumpToLevel(i)}
                    style={{ cursor: "pointer" }}
                  >
                    <span className={levelMap[h.userType] || ""} />
                    <strong>{h.username}</strong>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="pop-content">
            <div className="pop-title">
              {isSportsBook ? (
                <table className="table01 table-responsive">
                  <tbody>
                    <tr>
                      <th rowSpan={2} className="align-L">
                        Downline
                      </th>
                      <th className="align-C border-l bgcolor-sub" colSpan={2}>
                        Player P/L
                      </th>
                    </tr>
                    <tr>
                      <th className="border-l bgcolor-sub">Lose</th>
                      <th className="border-l bgcolor-sub">Profit</th>
                    </tr>
                  </tbody>

                  {!isLoader ? (
                    <tbody>
                      {isEmpty(sportPremiumData) ? (
                        <tr>
                          <td colSpan={3} className="no-data">
                            <p>No Data</p>
                          </td>
                        </tr>
                      ) : (
                        sportPremiumData.map((row, i) => (
                          <tr key={row?.profileData?._id || i}>
                            <td className="td-UID">
                              <span className="order">{i + 1}.</span>
                              <span
                                className={`Bdlevel ${
                                  levelMap[row?.profileData?.userType] || ""
                                }`}
                              ></span>
                              {current?.userType === "agent" ? (
                                <a href="#" className="text-primary">
                                  {row?.profileData?.username || null}
                                </a>
                              ) : (
                                <a
                                  href="#"
                                  className="text-primary"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    drillIntoSportsBook(row);
                                  }}
                                >
                                  {row?.profileData?.username || null}
                                </a>
                              )}
                            </td>
                            <td>
                              <span className="green">
                                {toFixedWithoutRounding(
                                  row?.positionLoseAmount || 0,
                                )}
                              </span>
                            </td>
                            <td>
                              <span className="red">
                                (-
                                {toFixedWithoutRounding(
                                  row?.positionProfitAmount || 0,
                                )}
                                )
                              </span>
                            </td>
                          </tr>
                        ))
                      )}

                      {!isEmpty(sportPremiumData) && (
                        <tr className="total">
                          <td className="align-L">Total</td>
                          <td>
                            <span className="green">
                              {toFixedWithoutRounding(sportPremiumTotals.lose)}
                            </span>
                          </td>
                          <td>
                            <span className="red">
                              (-
                              {toFixedWithoutRounding(
                                sportPremiumTotals.profit,
                              )}
                              )
                            </span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan={3}>
                          <div
                            id="loader"
                            className="spinner"
                            style={{ display: "block" }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              ) : (
                <table className="table01 table-responsive">
                  <tbody>
                    <tr>
                      <th rowSpan={2} className="align-L">
                        Downline
                      </th>
                      <th
                        className="align-C border-l bgcolor-sub"
                        colSpan={runnerDraw ? 3 : 2}
                      >
                        Player P/L
                      </th>
                    </tr>
                    <tr>
                      <th className="border-l bgcolor-sub">
                        {runner1?.RunnerName}
                      </th>
                      {runnerDraw && (
                        <th className="border-l bgcolor-sub">The Draw</th>
                      )}
                      <th className="border-l bgcolor-sub">
                        {runner2?.RunnerName}
                      </th>
                    </tr>
                  </tbody>

                  {!isLoader ? (
                    <tbody>
                      {isEmpty(matchData) ? (
                        <tr>
                          <td colSpan={4} className="no-data">
                            <p>No Data</p>
                          </td>
                        </tr>
                      ) : (
                        matchData.map((row, i) => {
                          const first = positionFor(row, runner1?.SelectionId);
                          const second = positionFor(row, runner2?.SelectionId);
                          const draw = runnerDraw
                            ? positionFor(row, runnerDraw?.SelectionId)
                            : 0;

                          return (
                            <tr key={row?._id || i}>
                              <td className="td-UID">
                                <span className="order">{i + 1}.</span>
                                <span
                                  className={`Bdlevel ${levelMap[row?.userType]}`}
                                ></span>
                                {current?.userType === "agent" ? (
                                  <Link
                                    to={`/my-account/account-summary/${row?._id}/user`}
                                    className="text-primary"
                                  >
                                    {row?.username || null}
                                  </Link>
                                ) : (
                                  <a
                                    href="#"
                                    className="text-primary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      drillInto(row);
                                    }}
                                  >
                                    {row?.username || null}
                                  </a>
                                )}
                              </td>
                              <td>{plSpan(first)}</td>
                              {runnerDraw && <td>{plSpan(draw)}</td>}
                              <td>{plSpan(second)}</td>
                            </tr>
                          );
                        })
                      )}

                      {!isEmpty(matchData) && (
                        <tr className="total">
                          <td className="align-L">Total</td>
                          <td>{plSpan(totals.first)}</td>
                          {runnerDraw && <td>{plSpan(totals.draw)}</td>}
                          <td>{plSpan(totals.second)}</td>
                        </tr>
                      )}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan={4}>
                          <div
                            id="loader"
                            className="spinner"
                            style={{ display: "block" }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
